import { Box, Button, Heading, Spinner, Stack, useToast } from '@chakra-ui/react';
import { useUserContext } from 'providers/useUserContext';
import Auth from 'screens/Account/Auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GiftCard } from 'types/giftCard';
import { Service } from 'types/service';
import { GiftCardCheckout } from './GiftCardCheckout';
import { ServiceCheckout } from './ServiceCheckout';
import { useEffect, useState } from 'react';
import { HTTP } from 'utils/http';
import { useListServices } from 'hooks/useApiQueries';
import * as Sentry from '@sentry/react';
import { Navigate } from 'react-router-dom';

export interface GiftCardProduct extends GiftCard {
  type: 'giftcard';
  IsIntroOffer?: boolean;
}

export interface ServiceProduct extends Service {
  type: 'service';
  IsIntroOffer: boolean;
}

export type Product = ServiceProduct | GiftCardProduct;

// Define the expected structure of the cancel response
interface CancelResponse {
  success: boolean;
  message?: string;
  refunds?: { payment: string; id: string }[];
}

export function Checkout() {
  const { isLoggedIn } = useUserContext();
  const [isCheckingAlmaPayment, setIsCheckingAlmaPayment] = useState(false);
  const { refetch: refetchServices, data: services } = useListServices();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const toast = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const paymentId = params.get('pid');

  useEffect(() => {
    const checkAlmaPaymentStatus = async () => {
      if (paymentId) {
        // Démarrer un replay pour la vérification Alma
        Sentry.startSession();
        try {
          const response = await HTTP.get<{ success: boolean }>('/sale/checkalmapaymentstatus', {
            pid: paymentId,
          }).send();

          Sentry.captureMessage('Checked Alma payment status', {
            level: 'info',
            tags: {
              category: 'payment',
              paymentMethod: 'alma',
            },
            extra: {
              paymentId: paymentId,
              response: response,
            },
          });

          return response?.success;
        } catch (error) {
          console.error('Error checking payment status:', error);

          Sentry.withScope((scope) => {
            scope.setTag('where', 'Checkout:checkAlmaPaymentStatus');
            scope.setLevel('error');
            scope.setContext('payment', {
              paymentId,
              sessionPaymentData: sessionStorage.getItem('paymentData'),
            });
            Sentry.captureException(error);
          });

          toast({
            title: 'Erreur de validation',
            description:
              'Erreur lors de la vérification du statut du paiement Alma. Si un paiement a été fait, vous recevrez un remboursement.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });

          // Roll back paymentId
          sessionStorage.removeItem('paymentId');

          // Roll back transaction in Alma
          try {
            const cancelResponse = await HTTP.get<CancelResponse>('/sale/rollbackalmapayment', {
              pid: paymentId,
            }).send();

            const refund = cancelResponse.refunds?.find((r) => r.payment === paymentId);

            if (!refund || !refund.id) {
              console.error('Rollback failed: Refund not found or ID missing');
              toast({
                title: 'Erreur de remboursement',
                description: 'Aucun remboursement trouvé ou ID manquant.',
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
            }
          } catch (error) {
            console.error('Error during rollback:', error);
            Sentry.withScope((scope) => {
              scope.setTag('where', 'Checkout:rollbackAlmaPayment');
              scope.setExtra('paymentId', paymentId);
              Sentry.captureException(error);
            });

            toast({
              title: 'Erreur inattendue',
              description: 'Une erreur inattendue est survenue lors du rollback.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }

          navigate('/tarifs', { replace: true });
          return false;
        }
      }
      return false;
    };

    const fetchPaymentStatus = async () => {
      setIsCheckingAlmaPayment(true);
      const paymentStatus = await checkAlmaPaymentStatus();

      if (paymentStatus) {
        const Payment = sessionStorage.getItem('paymentData');

        Sentry.captureMessage('Alma Retrieved payment data from session', {
          level: 'info',
          tags: {
            category: 'payment',
          },
          extra: {
            paymentData: Payment,
          },
        });

        if (Payment && paymentStatus) {
          await refetchServices();
          const paymentData = JSON.parse(Payment);

          try {
            Sentry.captureMessage('Alma Posting service purchase', {
              level: 'info',
              tags: {
                category: 'payment',
              },
              extra: {
                serviceId: paymentData.ServiceId,
                paymentMethod: paymentData.PaymentMethod,
              },
            });

            const response = await HTTP.post('/sale/services', {
              ServiceId: paymentData.ServiceId,
              Payment: paymentData,
              Test: false,
            }).send();
            await refetchServices();

            sessionStorage.setItem('paymentId', paymentId?.toString() ?? '');

            Sentry.captureMessage('Posted service after Alma payment', {
              level: 'info',
              tags: {
                category: 'payment',
                paymentMethod: 'alma',
              },
              extra: {
                paymentId,
                serviceId: paymentData.ServiceId,
                paymentData,
                response,
              },
            });

            navigate('/account/', { replace: true });
          } catch (error: any) {
            Sentry.withScope((scope) => {
              scope.setTag('where', 'Checkout:postService');
              scope.setLevel('error');
              scope.setContext('payment', {
                paymentData,
                paymentId,
              });
              Sentry.captureException(error);
            });

            if (error?.Code >= 400) {
              const description =
                error.Message ===
                'mb.Core.BLL.ShoppingCart failed validation Client has hit the purchase count for this intro series.'
                  ? "Vous avez déjà acheté une offre d'essai dans le passé. Ce type d'offre n'est disponible qu'une seule fois à la vente pour un client. La transaction est annulée."
                  : 'Une erreur est survenue lors de la validation de la transaction. La transaction est annulée.';

              toast({
                title: 'Erreur de validation',
                description: description,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });

              // Roll back paymentId
              sessionStorage.removeItem('paymentId');
              // Roll back transaction in Alma
              await HTTP.get<CancelResponse>('/sale/rollbackalmapayment', {
                pid: paymentId,
              }).send();

              navigate('/tarifs', { replace: true });
            }
          }
        } else {
          Sentry.withScope((scope) => {
            scope.setTag('where', 'Checkout:fetchPaymentStatus');
            scope.setTag('error_type', 'invalid_payment_status');
            scope.setLevel('error');
            scope.setContext('payment_validation', {
              paymentId,
              sessionStorage: {
                keys: Object.keys(sessionStorage),
                paymentData: sessionStorage.getItem('paymentData'),
                paymentId: sessionStorage.getItem('paymentId'),
              },
              location: window.location.href,
            });
            Sentry.captureMessage('Alma payment validation failed - Invalid payment status');
          });

          toast({
            title: 'Erreur de validation',
            description: 'Aucun paiement trouvé',
            status: 'error',
          });
        }
      }
      setIsCheckingAlmaPayment(false);
    };

    if (paymentId) {
      const tempPaymentId = sessionStorage.getItem('paymentId');
      if (tempPaymentId === paymentId) {
        // Add breadcrumb for already processed payment
        Sentry.captureMessage('Payment already processed, redirecting to account', {
          level: 'info',
          tags: {
            category: 'payment',
          },
          extra: {
            paymentId: paymentId,
          },
        });

        navigate('/account/', { replace: true });
        return;
      } else {
        fetchPaymentStatus();
      }
    }
  }, [refetchServices, navigate, paymentId, toast]);

  // Récupérer le produit depuis l'URL si présent
  useEffect(() => {
    if (productId && services) {
      setIsLoading(true);
      const foundProduct = services.find((service) => service.Id === productId);
      if (foundProduct) {
        setProduct({ ...foundProduct, type: 'service', IsIntroOffer: false });
        navigate('/checkout', {
          state: { product: { ...foundProduct, type: 'service', IsIntroOffer: false } },
          replace: true,
        });
      } else {
        navigate('/tarifs');
      }
      setIsLoading(false);
    }
  }, [productId, services, navigate]);

  if (isCheckingAlmaPayment) {
    return (
      <Stack alignItems="center" justifyContent="center" height="50vh">
        <Spinner />
        <Heading size="sm" textAlign="center" margin="0 auto" maxWidth="700px">
          Il peut y avoir un décalage de quelques minutes avant que les crédits achetés via Alma se voient sur votre
          compte
        </Heading>
      </Stack>
    );
  }

  // Si on a un productId dans l'URL mais que le produit n'est pas encore chargé
  if (productId && isLoading) {
    return <Spinner size="xl" />;
  }

  // Utiliser le produit de l'URL ou celui du state de location
  const checkoutProduct = product || location.state?.product;

  if (!checkoutProduct) {
    return <Navigate to="/tarifs" replace />;
  }

  if (!isLoggedIn) {
    return <Auth />;
  }

  return (
    <Stack className="CheckoutSummary">
      <Stack
        spacing={4}
        width="100%"
        margin="1em 0 2em"
        direction={{ base: 'column-reverse', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'center', md: 'flex-start' }}
      >
        <Heading
          className="accountMainTitle"
          size="md"
          textTransform="uppercase"
          textAlign={{ base: 'center', md: 'left' }}
        >
          Récapitulatif de commande
        </Heading>
        <Button variant="contained" alignSelf={{ base: 'flex-end', md: 'center' }} onClick={() => navigate(-1)}>
          Retour
        </Button>
      </Stack>

      {checkoutProduct.type === 'giftcard' ? (
        <Box className="GiftCardCheckout">
          <GiftCardCheckout product={checkoutProduct} />
        </Box>
      ) : (
        <Box className="ServiceCheckout">
          <ServiceCheckout product={checkoutProduct} />
        </Box>
      )}
    </Stack>
  );
}
